import React, { useState, useMemo } from 'react';
import { Heart, PlayCircle, X, Youtube, Film } from 'lucide-react';
import { getAuthHeaders } from '../../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Ring gradient + matching glow, cycled per favorite item — mirrors the mockup's per-tile palette
const RINGS = [
  'linear-gradient(135deg,#8b5cf6,#ec4899)',
  'linear-gradient(135deg,#0ea5e9,#8b5cf6)',
  'linear-gradient(135deg,#f59e0b,#ec4899)',
  'linear-gradient(135deg,#34d399,#0ea5e9)',
];
const GLOWS = [
  'rgba(139,92,246,0.35)',
  'rgba(14,165,233,0.35)',
  'rgba(245,158,11,0.35)',
  'rgba(52,211,153,0.3)',
];

// ── Favorite channel: circular tile with gradient ring, lift+glow on hover ──
const ChannelTile = ({ channel, idx, onChannelClick, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  const ring = RINGS[idx % RINGS.length];
  const glow = GLOWS[idx % GLOWS.length];

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
        background: '#181722', border: `1px solid ${hovered ? 'transparent' : '#232130'}`,
        borderRadius: '16px', padding: '16px 10px 12px', textAlign: 'center',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-6px) scale(1.05)' : 'none',
        boxShadow: hovered ? `0 14px 30px ${glow}` : 'none',
        transition: 'transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s ease, border-color 0.22s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onChannelClick ? onChannelClick(channel) : channel.channelUrl && window.open(channel.channelUrl, '_blank')}
    >
      {hovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(channel.channelId); }}
          style={{
            position: 'absolute', top: '6px', right: '6px', zIndex: 1,
            width: '20px', height: '20px', borderRadius: '50%',
            background: '#EF4444', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={12} color="#FFF" />
        </button>
      )}
      <div style={{ width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0, padding: '2px', background: ring }}>
        <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', border: '2px solid #181722', background: '#1F2937', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {channel.thumbnail
            ? <img src={channel.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Youtube size={20} color="#6f6b7d" />}
        </div>
      </div>
      <span style={{
        fontSize: '13px', fontWeight: '700', color: '#FFF', fontFamily: MANROPE,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
      }}>
        {channel.channelName}
      </span>
    </div>
  );
};

// ── Movie/show poster: lift + pink border + pink glow on hover ──
const PosterTile = ({ content, onContentClick, onRemove }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        position: 'relative', borderRadius: '14px', overflow: 'hidden', cursor: 'pointer',
        border: `1px solid ${hovered ? '#ec4899' : '#232130'}`,
        transform: hovered ? 'translateY(-8px) scale(1.04)' : 'none',
        boxShadow: hovered ? '0 20px 40px rgba(236,72,153,0.3)' : 'none',
        transition: 'transform 0.25s cubic-bezier(.34,1.56,.64,1), box-shadow 0.25s ease, border-color 0.25s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onContentClick && onContentClick(content)}
    >
      {/* Type badge */}
      <span style={{
        position: 'absolute', top: '8px', left: '8px', zIndex: 1,
        background: 'linear-gradient(135deg,#8b5cf6,#ec4899)', color: '#fff',
        fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px',
        letterSpacing: '0.03em', textTransform: 'uppercase',
      }}>
        {content.contentType}
      </span>

      <div style={{ aspectRatio: '2/3', position: 'relative', background: '#1F2937' }}>
        {content.poster
          ? <img src={content.poster} alt={content.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Film size={28} color="#6f6b7d" />
            </div>}

        <div style={{ position: 'absolute', inset: 'auto 0 0 0', height: '52%', background: 'linear-gradient(180deg,transparent,rgba(10,10,15,0.95))', pointerEvents: 'none' }} />
        <span style={{
          position: 'absolute', bottom: '10px', left: '10px', right: '10px',
          fontSize: '13px', fontWeight: '700', color: '#fff', lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {content.title}
        </span>

        {hovered && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(content.contentId, content.contentType); }}
            style={{
              position: 'absolute', top: '8px', right: '8px',
              width: '26px', height: '26px', borderRadius: '50%',
              background: 'rgba(0,0,0,0.7)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={14} color="#FFF" />
          </button>
        )}
      </div>
    </div>
  );
};

const FavoritesGrid = ({ favorites = [], contentLikes = [], onChannelClick, onContentClick }) => {
  const [removedChannelIds, setRemovedChannelIds] = useState(new Set());
  const [removedContentIds, setRemovedContentIds] = useState(new Set());
  const [removedReactionIds, setRemovedReactionIds] = useState(new Set());
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Shuffle once per data load (stable order within session, random on next visit)
  const shuffledChannels = useMemo(() =>
    shuffle(favorites.filter(f => f.type === 'channel')),
  [favorites]);

  const shuffledReactions = useMemo(() =>
    shuffle(favorites.filter(f => f.type === 'reaction')),
  [favorites]);

  const shuffledContent = useMemo(() =>
    shuffle(contentLikes),
  [contentLikes]);

  const channels = shuffledChannels.filter(c => !removedChannelIds.has(c.channelId));
  const reactions = shuffledReactions.filter(r => !removedReactionIds.has(r.videoId));
  const visibleContent = shuffledContent.filter(c => !removedContentIds.has(c.contentId));

  const totalFavorites = channels.length + reactions.length + visibleContent.length;

  const handleRemoveChannel = async (channelId) => {
    try {
      const r = await fetch(`${API_URL}/myspace/favorites/${channelId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (r.ok) setRemovedChannelIds(prev => new Set(prev).add(channelId));
    } catch (e) { console.error(e); }
  };

  const handleRemoveReaction = async (videoId) => {
    try {
      const r = await fetch(`${API_URL}/myspace/favorites/${videoId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (r.ok) setRemovedReactionIds(prev => new Set(prev).add(videoId));
    } catch (e) { console.error(e); }
  };

  const handleRemoveContent = async (contentId, contentType) => {
    try {
      const r = await fetch(`${API_URL}/myspace/like-content/${contentId}/${contentType}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (r.ok) setRemovedContentIds(prev => new Set(prev).add(contentId));
    } catch (e) { console.error(e); }
  };

  if (totalFavorites === 0) {
    return (
      <div style={{
        background: 'linear-gradient(180deg,#151420,#101018)', borderRadius: '16px',
        border: '1px dashed rgba(236,72,153,0.3)', padding: isMobile ? '28px 18px' : '40px', textAlign: 'center',
      }}>
        <Heart size={32} color="#EC4899" style={{ marginBottom: '12px', opacity: 0.5 }} />
        <p style={{ color: '#8f8b9c', fontSize: '14px', margin: 0, fontFamily: MANROPE }}>No favorites yet. Start exploring!</p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(180deg,#151420,#101018)',
      borderRadius: isMobile ? '16px' : '20px',
      border: '1px solid #232130',
      padding: isMobile ? '16px' : '22px',
      minWidth: 0,
      boxSizing: 'border-box',
      boxShadow: '0 16px 32px rgba(0,0,0,0.22)',
      fontFamily: MANROPE,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '22px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
            <Heart size={18} color="#EC4899" />
            <span style={{ color: '#FFF', fontSize: '17px', fontWeight: '700', fontFamily: SORA }}>Favorites</span>
            <span style={{ color: '#6f6b7d', fontSize: '13px' }}>({totalFavorites})</span>
          </div>
          <p style={{ color: '#a8a5b4', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
            Your saved creators, reaction videos, and media picks in one place.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '18px' : '24px', minWidth: 0 }}>

        {/* ── Channels: wrapping grid of circular tiles, no pagination ── */}
        {channels.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <Youtube size={14} color="#EF4444" />
              <span style={{ color: '#8f8b9c', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Channels ({channels.length})
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px' }}>
              {channels.map((ch, i) => (
                <ChannelTile key={ch.channelId || i} channel={ch} idx={i} onChannelClick={onChannelClick} onRemove={handleRemoveChannel} />
              ))}
            </div>
          </div>
        )}

        {/* ── Movies & Shows: wrapping grid, no pagination ── */}
        {visibleContent.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <Film size={14} color="#3B82F6" />
              <span style={{ color: '#8f8b9c', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Movies & Shows ({visibleContent.length})
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: isMobile ? '10px' : '16px' }}>
              {visibleContent.map((content, index) => (
                <PosterTile key={content.contentId || index} content={content} onContentClick={onContentClick} onRemove={handleRemoveContent} />
              ))}
            </div>
          </div>
        )}

        {/* ── Reactions ── */}
        {reactions.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <PlayCircle size={14} color="#8B5CF6" />
                <span style={{ color: '#8f8b9c', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Saved Reactions ({reactions.length})
                </span>
              </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
              {reactions.slice(0, 6).map((reaction, index) => (
                <div
                  key={reaction.videoId || index}
                  style={{
                    display: 'flex', gap: '12px', padding: '10px',
                    background: hoveredItem === `rx-${reaction.videoId}` ? 'rgba(139,92,246,0.1)' : '#181722',
                    border: '1px solid #232130', borderRadius: '10px',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={() => setHoveredItem(`rx-${reaction.videoId}`)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={() => reaction.videoUrl && window.open(reaction.videoUrl, '_blank')}
                >
                  <div style={{ width: '100px', height: '56px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, background: '#1F2937' }}>
                    {reaction.thumbnail
                      ? <img src={reaction.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <PlayCircle size={20} color="#6f6b7d" style={{ margin: '18px 40px' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: '#FFF', fontSize: '13px', fontWeight: '500', margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {reaction.title}
                    </p>
                    <p style={{ color: '#6f6b7d', fontSize: '11px', margin: 0 }}>{reaction.channelName}</p>
                  </div>
                  {hoveredItem === `rx-${reaction.videoId}` && (
                    <X size={14} color="#EF4444" style={{ flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); handleRemoveReaction(reaction.videoId); }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FavoritesGrid;
